<template>
  <div
    class="modal"
    :class="{ 'is-active': value }">

    <div class="modal-background"
      @click="close()">
    </div>
    
    <div class="modal-content">
      <div class="modal-card">

        <header class="modal-card-head">
          <p class="modal-card-title">Manage Units</p>
          <button
            class="delete"
            aria-label="close"
            @click="close(true)">
          </button>
        </header>

        <div class="modal-card-body">

          <article 
            v-if="!loading && units.length == 0" 
            class="message is-info is-fullwidth mt-3">
            <div class="message-body">
              There are no units.
            </div>
          </article>

          <v-fade-transition mode="out-in">
            <div 
              v-if="loading" 
              class="is-relative">
              <v-spin-loader :value="true">
                <div class="py-6"></div>
              </v-spin-loader>
            </div>

            <table 
              v-else
              class="table is-fullwidth is-hoverable">

              <thead>
                <tr>
                  <th 
                    v-for="(header, headerIndex) in headers" 
                    :key="headerIndex">
                    {{ header.label }}
                  </th>
                  <th></th>
                </tr>
              </thead>

              <tfoot>
                <tr>
                  <td :colspan="headers.length + 1">
                    <div class="centered">
                      <button
                        v-if="!adding"
                        @click="adding = true" 
                        class="button is-primary is-small">
                        Add a new unit
                      </button>
                    </div>
                  </td>
                </tr>
              </tfoot>

              <tbody>
                <tr 
                  class="v-centered"
                  v-for="unit in units" 
                  :key="unit.id">

                  <template v-if="editingId != unit.id">
                    <td>
                      {{ unit.unit_name }}
                    </td>
                    <td>
                      {{ unit.unit_display_name }}
                    </td>
                    <td>
                      {{ unit.unit_desc }}
                    </td>
                  </template>

                  <template v-else>
                    <td>
                      <v-text-field 
                        required
                        placeholder="Enter Unit Name"
                        v-model="unit.unit_name">
                      </v-text-field>
                    </td>
                    <td>
                      <v-text-field 
                        required
                        placeholder="Enter Unit Display Name"
                        v-model="unit.unit_display_name">
                      </v-text-field>
                    </td>
                    <td>
                      <v-text-field 
                        placeholder="Enter Unit Description"
                        v-model="unit.unit_desc">
                      </v-text-field>
                    </td>
                  </template>
                  <td>
                    <div class="buttons buttons-right">
                      <template v-if="editingId != unit.id">
                        <button
                          @click="editingId = unit.id" 
                          class="button is-small">
                          <span class="icon">
                            <i class="fas fa-edit has-text-grey"></i>
                          </span>
                        </button>
                        <button
                          @click="launchConfirmDelete(unit.id)" 
                          class="button is-small">
                          <span class="icon">
                            <i class="fas fa-trash has-text-grey"></i>
                          </span>
                        </button>
                      </template>
                      <template v-else>
                        <button
                          :disabled="!unit.unit_name || !unit.unit_display_name"
                          @click="update(unit)" 
                          class="button is-primary is-small">
                          <span class="icon">
                            <i class="fas fa-save"></i>
                          </span>
                        </button>
                        <button
                          @click="editingId = null" 
                          class="button is-small">
                          <span class="icon">
                            <i class="fas fa-times has-text-grey"></i>
                          </span>
                        </button>
                      </template>
                    </div>
                  </td>
                </tr>

                <tr class="v-centered" v-if="adding">
                  <td>
                    <v-text-field 
                      required
                      placeholder="Enter Unit Name"
                      v-model="unit.unit_name">
                    </v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      required
                      placeholder="Enter Unit Display Name"
                      v-model="unit.unit_display_name">
                    </v-text-field>
                  </td>
                  <td>
                    <v-text-field 
                      placeholder="Enter Unit Description"
                      v-model="unit.unit_desc">
                    </v-text-field>
                  </td>
                  <td>
                    <div class="buttons buttons-right">
                      <button
                        :disabled="!unit.unit_name || !unit.unit_display_name"
                        @click="add" 
                        class="button is-primary is-small">
                        <span class="icon">
                          <i class="fas fa-save"></i>
                        </span>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </v-fade-transition>

        </div>

      </div>
    </div>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmedCb ? deleteConfirmedCb() : null"
      @cancel="confirmDelete.state = false"/>

  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
import VSpinLoader from '@/components/base/VSpinLoader'
import VConfirmModal from '@/components/base/VConfirmModal'
import VFadeTransition from '@/components/transitions/VFadeTransition'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean
    }
  },
  components: { VTextField, VSpinLoader, VConfirmModal, VFadeTransition },
  data() {
    return {
      headers: [
        { label: 'Name' },
        { label: 'Display Name' },
        { label: 'Description' } 
      ],
      unit: {
        unit_name: '',
        unit_display_name: null,
        unit_desc: ''
      },
      valid: false,
      adding: false,
      editingId: null,
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this service?',
        type: 'danger'
      },
      deleteConfirmedCb: null
    }
  },
  computed: {
    ...mapState("units", [
      "units", "loading"
    ])
  },
  watch: {
    value(v) {
      if (!v) {
        this.adding = false;
      }
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("units", [
      "getUnits","addUnit", "deleteUnit", "updateUnit",
      "toggleAllSelected"
    ]),
    ...mapMutations("units", [
      "selectAll", "deselectAll", "toggleSelected"
    ]),
    initialize() {
      if (this.units.length == 0) {
        this.getUnits();
      }
    },
    add() {
      this.addUnit(this.unit).then(() => {
        this.adding = false;
        this.clearData();
      });
    },
    update(unit) {
      this.updateUnit({ unitId: unit.id, data: unit }).then(() => {
        this.editingId = null;
      })
    },
    launchConfirmDelete(id) {
      this.deleteConfirmedCb = () => {
        this.deleteUnit(id);
        this.confirmDelete.state = false;
      }
      this.confirmDelete.message = 'Are you sure you want to delete this unit?';
      this.confirmDelete.state = true;
    },
    close(clear=false) {
      this.$emit('input', false);
      if (clear) {
        this.clearData();
      }
    },
    clearData() {
      Object.assign(this.$data, this.$options.data.call(this));
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';

.table tfoot td .centered {
  display: flex;
  flex-direction: row;
  justify-content: center;
}

.modal-card {
  border-radius: 6px;
}
</style>
